import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RedeemService from '../services/RedeemService';
import API from '../services/API';
import AddressForm from './AddressForm';

const RedeemCodeResult = ({ userInfo }) => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // State management
    const [transactionHistory, setTransactionHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [giftProgress, setGiftProgress] = useState('');
    const [approvedCount, setApprovedCount] = useState(0);
    const [totalCount, setTotalCount] = useState(0);

    // Latest user info statef
    const [latestUserInfo, setLatestUserInfo] = useState({
        id: null,
        name: null,
        loading: false,
        error: null
    });
    
    // Supervisor information state
    const [supervisorInfo, setSupervisorInfo] = useState({
        name: '',
        phone: '',
        loading: false,
        error: null
    });

    // Address form state
    const [showAddressForm, setShowAddressForm] = useState(false);
    const [savedAddress, setSavedAddress] = useState(null);
    const [hasCheckedAutoPopup, setHasCheckedAutoPopup] = useState(false);


    // Get URL parameters
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get('token');
    const phoneNumber = urlParams.get('phoneNumber');
    const mainUrl = urlParams.get('mainUrl');
    const resultUrl = urlParams.get('resultUrl');

    const calculateGiftProgress = useCallback((approvedCount, totalCount, hasAddress = false) => {
        const ordersNeededForGift = 10;
        const ordersNeededForNextGift = ordersNeededForGift - (approvedCount % ordersNeededForGift);

        if (totalCount >= 10) {
            if (hasAddress) {
                return `Chúc mừng! Quà tặng sẽ được gửi đến địa chỉ của bạn ngay sau khi hoàn tất duyệt 10 đơn hàng. Liên hệ đại lý để được duyệt đơn nhanh nhất`;
            } else {
                return `Chúc mừng! Bạn đã nhập mã thành công 10 sản phẩm. Vui lòng bấm vào đây để nhập địa chỉ nhận quà.`;
            }
        } else {
            return `Bạn cần ${ordersNeededForNextGift} đơn được duyệt nữa để nhận quà. Liên hệ Đại lý để được duyệt đơn nhanh nhất`;
        }
    }, []);

    // Function to fetch latest user information
    const fetchLatestUserInfo = useCallback(async (phoneNumber) => {
        if (!phoneNumber) {
            return;
        }

        setLatestUserInfo(prev => ({ ...prev, loading: true, error: null }));

        try {
            const result = await API.checkUserExists(phoneNumber);
            console.log('Latest user info result:', result);
            
            if (result.exists && result.userInfo) {
                setLatestUserInfo({
                    id: result.userInfo.id || result.userInfo.userId,
                    name: result.userInfo.name,
                    loading: false,
                    error: null
                });
            } else {
                setLatestUserInfo({
                    id: null,
                    name: null,
                    loading: false,
                    error: 'Không tìm thấy thông tin user'
                });
            }
        } catch (error) {
            console.error('Error fetching latest user info:', error);
            setLatestUserInfo({
                id: null,
                name: null,
                loading: false,
                error: 'Không thể tải thông tin user'
            });
        }
    }, []);

    // Function to fetch supervisor information
    const fetchSupervisorInfo = useCallback(async (supervisorPhone) => {
        if (!supervisorPhone) {
            return;
        }

        setSupervisorInfo(prev => ({ ...prev, loading: true, error: null }));

        try {
            const result = await API.checkUserExists(supervisorPhone);
            console.log('Supervisor info result:', result);
            
            if (result.exists && result.userInfo) {
                // Extract supervisor name from userInfo
                const supervisorName = result.userInfo.firstName && result.userInfo.lastName 
                    ? `${result.userInfo.firstName} ${result.userInfo.lastName}`.trim()
                    : result.userInfo.fullName || result.userInfo.name || 'Đại lý';
                
                setSupervisorInfo({
                    name: supervisorName,
                    phone: supervisorPhone,
                    loading: false,
                    error: null
                });
            } else {
                setSupervisorInfo({
                    name: 'Đại lý',
                    phone: supervisorPhone,
                    loading: false,
                    error: 'Không tìm thấy thông tin đại lý'
                });
            }
        } catch (error) {
            console.error('Error fetching supervisor info:', error);
            setSupervisorInfo({
                name: 'Đại lý',
                phone: supervisorPhone,
                loading: false,
                error: 'Không thể tải thông tin đại lý'
            });
        }
    }, []);

    // Function to handle address form submission
    const handleAddressSubmit = async (addressData) => {
        try {
            const userPhoneNumber = userInfo?.phoneNumber || phoneNumber;
            const result = await API.saveUserAddress(userPhoneNumber, addressData);
            
            if (result.success) {
                setSavedAddress(addressData);
                setShowAddressForm(false);
                // Address is now saved, so auto-popup check is complete
            }
        } catch (error) {
            console.error('Error saving address:', error);
            alert('Có lỗi xảy ra khi lưu địa chỉ. Vui lòng thử lại.');
        }
    };

    // Function to handle address form cancellation
    const handleAddressCancel = () => {
        setShowAddressForm(false);
        console.log('User dismissed address form');
        // Don't reset hasCheckedAutoPopup to prevent showing popup again
    };


    // Function to check if user has saved address
    const checkSavedAddress = useCallback(async () => {
        const userPhoneNumber = userInfo?.phoneNumber || phoneNumber;
        console.log('checkSavedAddress userPhoneNumber:', userPhoneNumber);
        if (!userPhoneNumber) return;
        
        try {
            const result = await API.getUserInfo(userPhoneNumber);
            console.log('checkSavedAddress result:', result);
            if (result.userData && result.userData.address) {
                setSavedAddress({
                    streetAddress: result.userData.address,
                    province: result.userData.city,
                    district: result.userData.district || '',
                    ward: result.userData.ward || '',
                    fullName: result.userData.name,
                });
            }
        } catch (error) {
            console.error('Error checking saved address:', error);
        }
    }, [userInfo?.phoneNumber, phoneNumber]);

    const calculateOrderCounts = useCallback((transactions) => {
        const total = transactions.length;
        const approved = transactions.filter(transaction => 
            transaction.status === 'Đã duyệt' || transaction.status === 'Thành công'
        ).length;
        
        setTotalCount(total);
        setApprovedCount(approved);
        
        // Don't calculate gift progress here to avoid dependency loop
        // It will be calculated in separate useEffect
    }, []);

    const loadTransactionHistory = useCallback(async () => {
        try {
            // Load from API - API already returns all transactions
            const data = await RedeemService.getMyRedeemedCodes();
            const transactions = data.transactions || [];

            setTransactionHistory(transactions);
            calculateOrderCounts(transactions);
        } catch (error) {
            console.error('Error loading transaction history:', error);
            // Use fallback data
            const fallbackData = RedeemService.getFallbackTransactionHistory();
            setTransactionHistory(fallbackData.transactions);
            calculateOrderCounts(fallbackData.transactions);
        }
    }, [calculateOrderCounts]);

    const initializeSuccessScreen = useCallback(async () => {
        try {
            setLoading(true);
            
            // Set customer info
            // setCustomerInfo({
            //     phoneNumber: phoneNumber || '',
            //     token: token || ''
            // });

            // Load transaction history first to get counts
            await loadTransactionHistory();
            
            // Check for saved address
            await checkSavedAddress();
            
            // Fetch latest user info
            const userPhoneNumber = userInfo?.phoneNumber || phoneNumber;
            if (userPhoneNumber) {
                await fetchLatestUserInfo(userPhoneNumber);
            }
            
            console.log('userInfo', userInfo);
            console.log('userInfo.phoneParent', userInfo?.phoneParent);

        } catch (error) {
            console.error('Error initializing success screen:', error);
        } finally {
            setLoading(false);
        }
    }, [loadTransactionHistory, checkSavedAddress, fetchLatestUserInfo, userInfo, phoneNumber]);


    useEffect(() => {
        initializeSuccessScreen();
    }, [initializeSuccessScreen]);

    // Update gift progress when counts or address changes
    useEffect(() => {
        if (totalCount > 0) {
            const giftProgressText = calculateGiftProgress(approvedCount, totalCount, !!savedAddress);
            setGiftProgress(giftProgressText);
        }
    }, [savedAddress, approvedCount, totalCount, calculateGiftProgress]);


    // Fetch supervisor info when userInfo.phoneParent changes
    useEffect(() => {
        if (userInfo?.phoneParent) {
            fetchSupervisorInfo(userInfo.phoneParent);
        }
    }, [userInfo?.phoneParent, fetchSupervisorInfo]);

    // Auto-show address form popup when customer has >= 10 codes but no saved address
    useEffect(() => {
        // Only check once after data is loaded and avoid repeated calls
        if (!loading && !hasCheckedAutoPopup && totalCount >= 10 && !savedAddress && !showAddressForm) {
            console.log('Auto-showing address form: totalCount =', totalCount, 'savedAddress =', savedAddress);
            setShowAddressForm(true);
            setHasCheckedAutoPopup(true);
        }
    }, [loading, hasCheckedAutoPopup, totalCount, savedAddress, showAddressForm]);

    const handleNewPurchase = () => {
        // Redirect back to redeem form
        const redeemFormParams = new URLSearchParams({
            token: token || '',
            phoneNumber: phoneNumber || '',
            mainUrl: mainUrl || '',
            resultUrl: resultUrl || ''
        });
        navigate(`/redeem-code-form?${redeemFormParams.toString()}`);
    };


    const getStatusIcon = (status) => {
        switch (status) {
            case 'Chờ duyệt':
                return (
                    <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        className="text-yellow-500"
                        title="Chờ duyệt"
                    >
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12,6 12,12 16,14"></polyline>
                    </svg>
                );
            case 'Đã duyệt':
            case 'Thành công':
                return (
                    <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        className="text-green-500"
                        title={status}
                    >
                        <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
                        <path d="m9 12 2 2 4-4"></path>
                    </svg>
                );
            case 'Bị từ chối':
                return (
                    <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        className="text-red-500"
                        title="Bị từ chối"
                    >
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="15" y1="9" x2="9" y2="15"></line>
                        <line x1="9" y1="9" x2="15" y2="15"></line>
                    </svg>
                );
            default:
                return (
                    <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        className="text-gray-500"
                        title={status}
                    >
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="m9 12 2 2 4-4"></path>
                    </svg>
                );
        }
    };

    if (loading) {
        return (
            <div className="gradient-bg min-h-screen flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                    <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Đang tải dữ liệu...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="gradient-bg min-h-screen flex items-center justify-center p-4">
            <div className="w-full max-w-4xl">
                <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 fade-in">
                    <div className="text-center">
                        <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="64" 
                            height="64" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            className="mx-auto text-green-500 mb-2"
                        >
                            <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
                            <path d="m9 12 2 2 4-4"></path>
                        </svg>
                        <h1 className="text-2xl font-bold text-gray-800">Nhập mã thành công</h1>
                        {latestUserInfo.loading ? (
                            <p className="text-gray-600 mt-2">Đang tải thông tin user...</p>
                        ) : latestUserInfo.error ? (
                            userInfo?.name && (
                                <p className="text-gray-600 mt-2"><span className="font-semibold" style={{ color: '#277f4d' }}>{userInfo.id}-{userInfo.name}</span></p>
                            )
                        ) : latestUserInfo.id && latestUserInfo.name ? (
                            <p className="text-gray-600 mt-2"><span className="font-semibold" style={{ color: '#277f4d' }}>{latestUserInfo.id}-{latestUserInfo.name}</span></p>
                        ) : userInfo?.name && (
                            <p className="text-gray-600 mt-2"><span className="font-semibold" style={{ color: '#277f4d' }}>{userInfo.id}-{userInfo.name}</span></p>
                        )}
                    </div>
                    
                    <div className="mt-6 text-center font-semibold p-3 rounded-lg" style={{ backgroundColor: '#f0f9f4', color: '#277f4d' }}>
                        {giftProgress.includes('Vui lòng bấm vào đây') ? (
                            <button 
                                onClick={() => {
                                    setShowAddressForm(true);
                                }}
                                className="underline cursor-pointer hover:opacity-80"
                                style={{ color: '#277f4d' }}
                            >
                                {giftProgress}
                            </button>
                        ) : (
                            <span>{giftProgress}</span>
                        )}
                    </div>
                    
                    {/* Warning message for customers with >= 20 codes */}
                    {totalCount >= 20 && (
                        <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                            <div className="flex items-start">
                                <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="20" 
                                    height="20" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    className="text-orange-500 mr-2 mt-0.5 flex-shrink-0"
                                >
                                    <path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
                                    <path d="M12 9v4"></path>
                                    <path d="m12 17 .01 0"></path>
                                </svg>
                                <p className="text-sm text-orange-800 font-medium">
                                    <span className="font-semibold">Cảnh báo:</span> Bạn đã quét đủ số mã tối đa cho đợt này. Nếu quét tiếp cũng sẽ không nhận được thêm quà và cũng không được cộng dồn cho đợt sau.
                                </p>
                            </div>
                        </div>
                    )}
                    
                    {/* Show notification when address form is auto-opened */}
                    {showAddressForm && approvedCount >= 10 && !savedAddress && (
                        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-center">
                                <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="20" 
                                    height="20" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    className="text-blue-500 mr-2"
                                >
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <path d="m9 12 2 2 4-4"></path>
                                </svg>
                                <p className="text-sm text-blue-800 font-medium">
                                    Chúc mừng! Bạn đã đủ điều kiện nhận quà. Vui lòng nhập địa chỉ để nhận quà tặng.
                                </p>
                            </div>
                        </div>
                    )}
                    
                    {/* Display saved address if available */}
                    {savedAddress && (
                        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <h4 className="text-sm font-medium text-green-800 mb-1">Địa chỉ nhận quà đã lưu:</h4>
                                    {savedAddress.fullName && (
                                        <p className="text-sm font-medium text-green-800 mb-1">
                                            {savedAddress.fullName}
                                        </p>
                                    )}
                                    <p className="text-sm text-green-700">
                                        {savedAddress.streetAddress}
                                        {savedAddress.ward && `, ${savedAddress.ward}`}
                                        {savedAddress.district && `, ${savedAddress.district}`}
                                        {savedAddress.province && `, ${savedAddress.province}`}
                                    </p>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowAddressForm(true);
                                    }}
                                    className="ml-3 text-xs text-green-600 hover:text-green-800 underline"
                                >
                                    Sửa địa chỉ
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="mt-6">
                        <div className="flex justify-between items-center mb-2">
                            <h2 className="text-lg font-semibold text-gray-700">Lịch sử mua hàng</h2>
                            <div className="flex items-center gap-1 text-sm font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded-md">
                                {getStatusIcon('Đã duyệt')}
                                <span>Đã duyệt {approvedCount}/{totalCount}</span>
                            </div>
                        </div>
                        <div className="overflow-x-auto max-h-64 border rounded-lg">
                            <table className="min-w-full text-sm text-left">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="p-3">Sản phẩm</th>
                                        <th className="p-3">Giá</th>
                                        <th className="p-3">Ngày mua</th>
                                        <th className="p-3 w-16 text-center">Trạng thái</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white">
                                    {transactionHistory.map((transaction, index) => (
                                        <tr key={transaction.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                            <td className="p-3">
                                                {transaction.product}
                                            </td>
                                            <td className="p-3">{transaction.price}</td>
                                            <td className="p-3">{transaction.date}</td>
                                            <td className="p-3 text-center">
                                                <div className="flex justify-center">
                                                    {getStatusIcon(transaction.status)}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="mt-6 text-center text-xs text-gray-500 border-t pt-4">
                        {supervisorInfo.loading ? (
                            <p>Đang tải thông tin đại lý...</p>
                        ) : supervisorInfo.error ? (
                            <p>Hãy liên hệ đại lý qua SĐT <b>{supervisorInfo.phone}</b> để được hỗ trợ nếu cần</p>
                        ) : (
                            <p>Hãy liên hệ đại lý <b>{supervisorInfo.name}</b> qua SĐT <b>{supervisorInfo.phone}</b> để được hỗ trợ nếu cần</p>
                        )}
                    </div>
                    
                    <div className="mt-6">
                        <button 
                            onClick={handleNewPurchase}
                            className="w-full text-white font-semibold py-3 px-4 rounded-lg transition-transform hover:scale-105"
                            style={{ backgroundColor: '#277f4d' }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#1f5f3a'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#277f4d'}
                        >
                            Nhập thêm mã phủ cào
                        </button>
                    </div>
                </div>
            </div>

            {/* Address Form Modal */}
            <AddressForm
                isVisible={showAddressForm}
                onSubmit={handleAddressSubmit}
                onCancel={handleAddressCancel}
                initialData={savedAddress}
                userInfo={userInfo}
            />
        </div>
    );
};

export default RedeemCodeResult;
